import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaBuilding } from 'react-icons/fa';

const Experience = () => {

  const experiences = [
      {
          "id": 1,
          "company" : "Nitwebs",
          "position" : "Senior Laravel Developer",
          "location" : "Ludhiana, Punjab, India",
          "duration" : "May 2024 - Present",
          "responsibilities" : [
              "Leading development of scalable Laravel-based SaaS applications with modular architecture",
              "Supervising project lifecycles from requirement gathering to deployment",
              "Managing and mentoring junior developers, ensuring adherence to coding standards and best practices",
              "Implementing CI/CD pipelines for smooth deployment processes",
              "Integrating cloud storage solutions and third-party APIs for business automation",
              "Optimizing MySQL queries and database structures for high-performance applications",
              "Collaborating with stakeholders and clients to align business goals with technical solutions"
          ],
          "skills_used" : ["PHP", "Laravel", "Vue.js", "Alpine.js", "Livewire", "GitHub", "DigitalOcean", "AWS S3", "MySQL", "Queue & Events", "Caching", "REST APIs"]
      },
      {
          "id": 2,
          "company" : "Code Rumble Pvt Ltd (Agile Continental Pvt Ltd)",
          "position" : "Senior Laravel Developer",
          "location" : "Ludhiana, Punjab, India",
          "duration" : "March 2021 - April 2024",
          "responsibilities" : [
              "Architected scalable SaaS applications with Laravel, Vue.js, and AWS services.",
              "Led refactoring of legacy monolith into modular Laravel components.",
              "Implemented caching (Redis) and optimized MySQL queries reducing load times by 60%.",
              "Mentored junior developers in clean coding, design patterns, and testing practices.",
              "Designed CI/CD pipelines using GitHub Actions for automated deployments."
          ],
          "skills_used" : ["Laravel", "AWS", "Redis", "MySQL Optimization", "Ajax", "CSS", "HTML", "GIT", "Jira", "Bitbucket"]
      },
      {
          "id": 3,
          "company" : "Emobx Mobility Experts",
          "position" : "PHP Developer",
          "location" : "Mohali, Punjab, India",
          "duration" : "May 2020 - March 2021",
          "responsibilities" : [
              "Developed and maintained Laravel-based enterprise applications for mobility services",
              "Optimized backend performance by implementing caching and queue-driven jobs",
              "Designed modular APIs for integration with mobile applications",
              "Collaborated with UI/UX team to build responsive front-end modules using Vue.js",
              "Ensured secure code practices and implemented role-based access control (RBAC)"
          ],
          "skills_used" : ["PHP", "Laravel", "Ajax", "MySQL", "Git", "Jira", "Bitbucket", "API Development", "Caching", "Queues"]
      },
      {
          "id": 4,
          "company" : "Technocrats Horizons Compusoft Pvt Ltd",
          "position" : "PHP Developer",
          "location" : "Ludhiana, Punjab, India",
          "duration" : "March 2018 - May 2020",
          "responsibilities" : [
              "Developed e-commerce and enterprise solutions using Laravel and WordPress",
              "Migrated legacy applications to Laravel with cleaner architecture and optimized queries",
              "Implemented custom payment gateway integrations (PayPal, Stripe, Authorize.Net)",
              "Handled deployment and server configuration on DigitalOcean",
              "Collaborated with cross-functional teams using Agile methodologies and Jira"
          ],
          "skills_used" : ["PHP", "Laravel", "WordPress", "MySQL", "JavaScript", "jQuery", "Git", "Jira", "Bitbucket", "DigitalOcean", "Payment Gateways"]
      },
      {
          "id": 5,
          "company" : "Destine Global Solutions Pvt Ltd",
          "position" : "Team Lead / Senior PHP Developer",
          "location" : "Mohali, Punjab, India", 
          "duration" : "July 2017 - March 2018",
          "responsibilities" : [
              "Led a team of 6 developers in delivering enterprise Laravel applications.",
              "Designed job queues and event-driven architecture for background processing.",
              "Collaborated directly with clients for requirement gathering and system architecture."
          ],
          "skills_used" : ["Laravel", "Queues", "Events", "API Development", "Git", "Agile"]
      },
      {
          "id": 6,
          "company" : "Esferasoft Solutions Pvt Ltd",
          "position" : "PHP Developer",
          "location" : "Mohali, Punjab, India",
          "duration" : "November 2014 - June 2017",
          "responsibilities" : [
              "Built and customized CMS platforms and e-commerce solutions in WordPress and Core PHP",
              "Contributed to full-stack development of client projects with custom themes and plugins",
              "Collaborated with design teams to deliver responsive and SEO-optimized websites",
              "Integrated third-party APIs and optimized database queries for improved performance",
              "Maintained version control and contributed to team projects using Git"
          ],
          "skills_used" : ["PHP", "WordPress", "MySQL", "jQuery", "Ajax", "HTML", "CSS", "Git"]
      },
      {
          "id": 7,
          "company" : "Renovate ERA Web Solutions Pvt Ltd",
          "position" : "Junior PHP Developer",
          "location" : "Ludhiana, Punjab, India",
          "duration" : "May 2013 - November 2014",
          "responsibilities": [
              "Assisted in the development of custom WordPress websites and PHP-based applications",
              "Gained hands-on experience with backend logic, database queries, and plugin integration",
              "Debugged and fixed issues in live applications to ensure smooth functionality",
              "Collaborated in a team environment, contributing to small-to-medium scale client projects"
          ],
          "skills_used" : ["PHP", "WordPress", "MySQL", "Ajax", "HTML", "CSS"]
      }
  ];

  return (
    <section id="experience" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Professional Experience
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            A journey through 10+ years of web development across multiple companies and technologies.
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 md:left-1/2 transform md:-translate-x-px top-0 bottom-0 w-0.5 bg-blue-200"></div>

          {/* Experience Cards */}
          <div className="space-y-12">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-6 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-blue-600 rounded-full border-4 border-white shadow-lg z-10"></div>

                {/* Content Card */}
                <div className={`w-full md:w-5/12 ml-16 md:ml-0 ${
                  index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                }`}>
                  <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                    {/* Header */}
                    <div className="mb-6">
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                        {exp.position}
                      </h3>
                      <div className="flex items-center text-blue-600 font-semibold mb-2">
                        <FaBuilding className="mr-2" />
                        <span className="text-lg">{exp.company}</span>
                      </div>
                      <div className="flex flex-col md:flex-row md:items-center text-gray-600 space-y-1 md:space-y-0 md:space-x-4">
                        <div className="flex items-center">
                          <FaCalendarAlt className="mr-2 text-sm" />
                          <span>{exp.duration}</span>
                        </div>
                        <div className="flex items-center">
                          <FaMapMarkerAlt className="mr-2 text-sm" />
                          <span>{exp.location}</span>
                        </div>
                      </div>
                    </div>

                    {/* Responsibilities */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        Key Responsibilities:
                      </h4>
                      <ul className="space-y-2">
                        {exp.responsibilities.map((responsibility, respIndex) => (
                          <li key={respIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-gray-700">{responsibility}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Skills Used */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">
                        Technologies Used:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {exp.skills_used.map((skill, skillIndex) => (
                          <span
                            key={skillIndex}
                            className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-16 bg-white p-8 rounded-xl shadow-lg"
        >
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Career Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">6</div>
              <div className="text-gray-600">Companies</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">10.5+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">8.5</div>
              <div className="text-gray-600">Years Laravel</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">Team</div>
              <div className="text-gray-600">Leadership</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;